import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { CreditCard, Zap, Check } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { getBillingStatus, createCheckoutSession, createPortalSession } from '@/api/billing'
import { ApiError } from '@/api/client'
import { useAuth } from '@/hooks/useAuth'
import { planLimitVars, usePlanLimits } from '@/hooks/usePlanLimits'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'

// 決済完了後、Webhook でプランが反映されるまでポーリングする間隔と上限
const UPGRADE_POLL_INTERVAL_MS = 2000
const UPGRADE_POLL_TIMEOUT_MS = 30000

export function BillingPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { t } = useTranslation()
  const { refreshUser } = useAuth()
  const [awaitingUpgrade, setAwaitingUpgrade] = useState(false)

  const limitVars = planLimitVars(usePlanLimits())
  const freeFeatures = t('billing.freeFeatures', { returnObjects: true, ...limitVars }) as string[]
  const proFeatures = t('billing.proFeatures', { returnObjects: true, ...limitVars }) as string[]

  const { data: status, isLoading } = useQuery({
    queryKey: ['billing-status'],
    queryFn: getBillingStatus,
    refetchInterval: awaitingUpgrade ? UPGRADE_POLL_INTERVAL_MS : false,
  })

  useEffect(() => {
    if (searchParams.get('success') === '1') {
      toast.success(t('billing.paymentSuccess'))
      setAwaitingUpgrade(true)
      setSearchParams({}, { replace: true })
    } else if (searchParams.get('canceled') === '1') {
      toast.info(t('billing.paymentCanceled'))
      setSearchParams({}, { replace: true })
    }
  }, [searchParams, setSearchParams, t])

  // Webhook の反映を確認できたらサイドバー等が参照するユーザー情報も更新する
  useEffect(() => {
    if (!awaitingUpgrade || !status || status.plan === 'free') return
    setAwaitingUpgrade(false)
    toast.success(t('billing.upgradeActivated'))
    refreshUser().catch(() => undefined)
  }, [awaitingUpgrade, status, refreshUser, t])

  useEffect(() => {
    if (!awaitingUpgrade) return
    const timer = setTimeout(() => {
      setAwaitingUpgrade(false)
      toast.info(t('billing.upgradeDelayed'))
    }, UPGRADE_POLL_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [awaitingUpgrade, t])

  const checkoutMutation = useMutation({
    mutationFn: () => createCheckoutSession(),
    onSuccess: ({ url }) => {
      if (url) window.location.href = url
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        toast.error(t('billing.subscriptionExists'))
        return
      }
      const message = err instanceof ApiError ? err.message : t('billing.checkoutFailed')
      toast.error(message)
    },
  })

  const portalMutation = useMutation({
    mutationFn: () => createPortalSession(),
    onSuccess: ({ url }) => {
      window.location.href = url
    },
    onError: (err) => {
      const message = err instanceof ApiError ? err.message : t('billing.portalFailed')
      toast.error(message)
    },
  })

  const isPro = status?.plan === 'pro' || status?.plan === 'team'
  // 未払いで Free に戻ったユーザーも支払い方法の更新のためポータルに入れるようにする
  const canManage = isPro || Boolean(status?.stripeSubscriptionId)

  return (
    <div className="flex-1 overflow-auto p-6">
      <div className="mx-auto max-w-2xl space-y-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('billing.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('billing.subtitle')}
          </p>
        </div>

        {/* 現在のプラン */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">{t('billing.currentPlan')}</CardTitle>
              <Badge variant={isPro ? 'default' : 'secondary'}>
                {isLoading ? '...' : isPro ? t('billing.proPlan') : t('billing.freePlan')}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            {isPro ? (
              <p className="text-sm text-muted-foreground">
                {t('billing.proDescription')}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t('billing.freeDescription')}
              </p>
            )}
          </CardContent>
          {canManage && (
            <CardFooter>
              <Button
                variant="outline"
                onClick={() => portalMutation.mutate()}
                disabled={portalMutation.isPending}
              >
                <CreditCard className="mr-2 h-4 w-4" />
                {portalMutation.isPending ? t('billing.upgrading') : t('billing.managePlan')}
              </Button>
            </CardFooter>
          )}
        </Card>

        {/* プラン比較 */}
        {!isPro && (
          <div className="grid gap-4 sm:grid-cols-2">
            {/* Free */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('billing.freePlan')}</CardTitle>
                <CardDescription>
                  <span className="text-2xl font-bold text-foreground">$0</span>
                  <span className="text-muted-foreground"> {t('billing.perMonth')}</span>
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {freeFeatures.map((f) => (
                  <div key={f} className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">{f}</span>
                  </div>
                ))}
              </CardContent>
              <CardFooter>
                <Button variant="outline" className="w-full" disabled>
                  {t('billing.currentPlan')}
                </Button>
              </CardFooter>
            </Card>

            {/* Pro */}
            <Card className="border-primary">
              <CardHeader>
                <CardTitle className="text-base">{t('billing.proPlan')}</CardTitle>
                <CardDescription>
                  <span className="text-2xl font-bold text-foreground">$9</span>
                  <span className="text-muted-foreground"> {t('billing.perMonth')}</span>
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {proFeatures.map((f) => (
                  <div key={f} className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-primary" />
                    <span>{f}</span>
                  </div>
                ))}
              </CardContent>
              <CardFooter className="flex-col items-stretch gap-3">
                {/* 特商法の最終確認画面(Stripe Checkout)と同じ要点を、購入ボタンの前にも示す(docs/22-commerce-disclosure-draft.md の「4.2」) */}
                <p className="text-xs leading-relaxed text-muted-foreground">
                  {t('billing.subscriptionTerms')}{' '}
                  <Link to="/legal/commerce" className="text-primary underline underline-offset-4">
                    {t('billing.legalNoticeLink')}
                  </Link>
                </p>
                <Button
                  className="w-full"
                  onClick={() => checkoutMutation.mutate()}
                  disabled={checkoutMutation.isPending || isLoading || awaitingUpgrade}
                >
                  <Zap className="mr-2 h-4 w-4" />
                  {awaitingUpgrade
                    ? t('billing.activating')
                    : checkoutMutation.isPending
                      ? t('billing.upgrading')
                      : t('billing.upgradePro')}
                </Button>
              </CardFooter>
            </Card>
          </div>
        )}
      </div>
    </div>
  )
}
